/**
 * A destructive action that asks in a dialog before doing it.
 *
 * Deleting a reconciliation removes a financial record, and deleting a mapping
 * discards setup somebody paid for once so they would not have to pay for it
 * again. Neither is recoverable.
 *
 * Built on the native `<dialog>` element rather than a div pretending to be
 * one. The browser gives focus trapping, Escape to dismiss, inert background
 * content, and correct semantics for assistive technology for free — all
 * things a hand-rolled overlay gets subtly wrong, and all things that matter
 * more than usual when the button at the end destroys data.
 *
 * Cancel takes focus, not Delete. A dialog that opens with the destructive
 * button focused turns a reflexive Enter into a deletion, which defeats the
 * point of asking.
 */
import { useEffect, useRef } from 'react';
import { Icon, type IconName } from './Icon';

export function ConfirmAction({
	label,
	confirmLabel,
	description,
	icon = 'trash',
	busy = false,
	className = 'btn btn-sm btn-error',
	compact = false,
	onConfirm,
}: {
	/** Text on the trigger. Also the tooltip when `compact`. */
	label: string;
	/** The question, naming the specific thing. Shown as the dialog's heading. */
	confirmLabel: string;
	/** What deleting actually costs — shown under the heading. */
	description?: string;
	icon?: IconName;
	busy?: boolean;
	className?: string;
	compact?: boolean;
	onConfirm(): void;
}) {
	const dialog = useRef<HTMLDialogElement>(null);

	// Close once the work finishes, so the dialog does not linger over a list
	// that has already lost the row it was asking about.
	useEffect(() => {
		if (!busy) dialog.current?.close();
	}, [busy]);

	return (
		<>
			{/*
			 * Compact renders the icon alone, so it carries a daisyUI tooltip rather
			 * than the native `title` it used to — `title` waits about a second, is
			 * unstyled, and is the one tooltip a touch device never shows. The
			 * `aria-label` stays: the tip is decoration, the label is the name.
			 */}
			<button
				className={compact ? 'btn btn-xs btn-ghost tooltip tooltip-left' : className}
				disabled={busy}
				aria-label={compact ? confirmLabel : undefined}
				data-tip={compact ? label : undefined}
				onClick={() => dialog.current?.showModal()}>
				<Icon name={icon} />
				{!compact && label}
			</button>

			<dialog ref={dialog} className="modal">
				{/*
				 * `text-start` is not decoration. A <dialog> renders in the browser's
				 * top layer but stays a DOM descendant of wherever it was declared,
				 * so it inherits that ancestor's styles — and the table cell these
				 * triggers sit in is `text-right`, which was right-aligning the
				 * dialog's prose. Stating alignment here makes the component
				 * independent of where it happens to be mounted.
				 */}
				<div className="modal-box border-base-300 border text-start">
					<h3 className="text-base font-semibold">{confirmLabel}</h3>
					{description !== undefined && (
						<p className="text-base-content/70 mt-2 text-sm">{description}</p>
					)}
					<p className="text-base-content/70 mt-2 text-sm">This cannot be undone.</p>

					<div className="modal-action">
						<button
							className="btn btn-sm btn-outline"
							autoFocus
							disabled={busy}
							onClick={() => dialog.current?.close()}>
							Cancel
						</button>
						<button className="btn btn-sm btn-error" disabled={busy} onClick={onConfirm}>
							{busy ? <span className="loading loading-spinner loading-xs" /> : 'Delete'}
						</button>
					</div>
				</div>

				{/* Clicking the backdrop dismisses, which is the safe outcome here. */}
				<form method="dialog" className="modal-backdrop">
					<button aria-label="Cancel">close</button>
				</form>
			</dialog>
		</>
	);
}
